import { useState } from "react";
import { useForm } from "react-hook-form";
import AppointmentFormFooter from "../Shared/AppointmentFormFooter";
import FormWrapper from "../Shared/FormWrapper";
import AlertRed from "../Shared/AlertRed";
import TextArea from "../Shared/TextArea";
import StatusDropdown from "../Shared/StatusDropdown";
import { APPOINTMENT_STATUS } from "../../config/appointment";
import { STATUS_COLOR_CLASSES } from "../../config/constants";
import { mapApiError } from "../../utils/formErrorMapper";

const CancelAppointmentForm = ({
  appointment,
  patchAppointment,
  closeDrawerHandler,
  onCancelHandler,
  setIsFormDirty,
}) => {
  const maxCharacters = 150;
  const [cancellationCharCount, setCancellationCharCount] = useState(0);
  const [banner, setBanner] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmit, setLastSubmit] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    setError,
  } = useForm({
    defaultValues: {
      status: APPOINTMENT_STATUS.find((status) => status.id === "cancelled")
        ?.id,
      cancellationReason: "",
    },
  });

  const currentStatus = APPOINTMENT_STATUS.find(
    (status) => status.id === watch("status")
  );

  const onSubmit = async (data, shouldNotifyClient = false) => {
    if (isSubmitting) return;
    setBanner(null);
    setLastSubmit({ data, shouldNotifyClient });
    setIsSubmitting(true);
    try {
      await patchAppointment({
        id: appointment.id,
        data: {
          status: data.status,
          cancellationReason: data.cancellationReason,
        },
        shouldNotifyClient,
      });
    } catch (err) {
      const result = mapApiError(err, { setError });
      if (result.handled) return;
      if (result.kind === "auth") return;
      setBanner(result.banner);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    handleSubmit((formData) => onSubmit(formData, false))();
  };

  const handleSaveAndEmail = async () => {
    handleSubmit((formData) => onSubmit(formData, true))();
  };

  const handleRetry = () => {
    if (lastSubmit) onSubmit(lastSubmit.data, lastSubmit.shouldNotifyClient);
  };

  return (
    <FormWrapper onSubmit={handleSubmit((formData) => onSubmit(formData, false))}>
      {banner ? (
        <div className="mb-4">
          <AlertRed
            title={banner.title}
            message={banner.message}
            onRetry={handleRetry}
          />
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="col-span-full">
          <StatusDropdown
            key={"Status"}
            label={
              <p className="flex items-center gap-2">
                <span
                  className={`${
                    STATUS_COLOR_CLASSES[currentStatus?.colour]
                  } forced-colors:bg-[Highlight] inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-transparent`}
                />
                Status
              </p>
            }
            data={APPOINTMENT_STATUS}
            defaultSelected={currentStatus}
            isReadOnly={true}
          />
        </div>
        <div className="col-span-full">
          <TextArea
            label="Reason for cancellation"
            optional
            id="cancellationReason"
            name="cancellationReason"
            placeholder="Let the client know why (e.g., illness, weather)"
            errormessage={errors?.cancellationReason?.message}
            charCount={cancellationCharCount}
            maxLength={maxCharacters}
            {...register("cancellationReason", {
              maxLength: {
                value: maxCharacters,
                message: `Cancellation reason cannot exceed ${maxCharacters} characters`,
              },
              onBlur: (e) => {
                setValue("cancellationReason", e.target.value.trim(), {
                  shouldValidate: true,
                });
              },
              onChange: (e) => {
                setCancellationCharCount(e.target.value.trim().length);
                setValue("cancellationReason", e.target.value, {
                  shouldValidate: true,
                });
              },
            })}
          />
        </div>
      </div>
      <AppointmentFormFooter
        secondaryCaption="Cancel"
        secondaryClickHandler={onCancelHandler}
        onSave={handleSave}
        onSaveAndEmail={handleSaveAndEmail}
        isLoading={isSubmitting}
        requiresVerification={false}
        mode="cancel"
      />
    </FormWrapper>
  );
};

export default CancelAppointmentForm;
